"use client"

import { useMemo } from "react"
import { View, StyleSheet } from "react-native"
import { Text, useTheme } from "react-native-paper"
import { PieChart, Pie, Cell, Legend, ResponsiveContainer, Tooltip, Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts"
import { HorizontalSection } from "../../NodeSummary/HorizontalSection"
import type { TabComponentProps } from "../../../types/tabs"
import { getAllNodesOfType, getNodesByParent, findNodesByName } from "../../../utils/assetNodeUtils"

interface ChartData {
    name: string
    value: number
    color: string
}

interface StackedData {
    structureType: string
    [key: string]: string | number
}

const COMPLETION_STATUS_COLORS: Record<string, string> = {
    "Yet To Start": "#4361ee",
    "Excavation": "#ef476f",
    "Concreting": "#06d6a0",
    "Back Filling": "#ffd60a",
    "Completed": "#8338ec",
    "Escavation": "#ef476f", // Alternative spelling
    "Backfilling": "#ffd60a", // Alternative spelling
}

const COLOR_PALETTE = [
    "#4361ee", // Blue
    "#ef476f", // Pink/Red
    "#06d6a0", // Teal
    "#ffd60a", // Yellow
    "#8338ec", // Purple
    "#ff9e00", // Orange
    "#3a86ff", // Royal Blue
    "#fb5607", // Vermilion
    "#118ab2", // Steel Blue
    "#073b4c", // Dark Teal
]

export const OverviewTab: React.FC<TabComponentProps> = ({ projectData, selectedNode }) => {
    const theme = useTheme()

    // Check if valid selection (line or section only)
    const isValidSelection = !selectedNode || selectedNode.type === "line" || selectedNode.type === "section"

    const { completionStatusData, structureTypeData, completionStatuses, spanCompletionData } = useMemo(() => {
        if (!projectData || !isValidSelection) {
            return { completionStatusData: [], structureTypeData: [], completionStatuses: [], spanCompletionData: [] }
        }

        // Get towers based on selection
        let towers: any[] = []

        if (selectedNode?.type === "section") {
            towers = getNodesByParent(projectData, selectedNode.name, "section", "tower")
        } else {
            // Line or no selection - get all towers
            towers = getAllNodesOfType(projectData, "tower")
            const uniqueTowersMap = new Map()
            towers.forEach(tower => {
                if (!uniqueTowersMap.has(tower.name)) {
                    uniqueTowersMap.set(tower.name, tower)
                }
            })
            towers = Array.from(uniqueTowersMap.values())
        }

        // Process completion status data for donut chart
        const statusCount: Record<string, number> = {}
        towers.forEach(tower => {
            const status = tower.properties?.["Completion Status"] || "Yet To Start"
            statusCount[status] = (statusCount[status] || 0) + 1
        })

        const totalTowers = towers.length
        const completionData: ChartData[] = Object.entries(statusCount).map(([name, value], index) => ({
            name,
            value,
            color: COMPLETION_STATUS_COLORS[name] || COLOR_PALETTE[index % COLOR_PALETTE.length],
        }))

        // Process structure type data for stacked column chart
        const uniqueCompletionStatuses = new Set<string>()
        const structureByType: Record<string, any> = {}

        towers.forEach(tower => {
            const structureType = tower.properties?.["Structure Name"] || "Unknown"
            const completionStatus = tower.properties?.["Completion Status"] || "Yet To Start"

            uniqueCompletionStatuses.add(completionStatus)

            if (!structureByType[structureType]) {
                structureByType[structureType] = {
                    structureType: structureType,
                    total: 0
                }
            }

            structureByType[structureType][completionStatus] =
                (structureByType[structureType][completionStatus] || 0) + 1
            structureByType[structureType].total += 1
        })

        const structureArray = Object.values(structureByType)

        // Process span completion status data
        let spans: any[] = []

        if (selectedNode?.type === "section") {
            spans = getNodesByParent(projectData, selectedNode.name, "section", "span")
        } else {
            // Line or no selection - get all spans
            spans = getAllNodesOfType(projectData, "span")
            const uniqueSpansMap = new Map()
            spans.forEach(span => {
                if (!uniqueSpansMap.has(span.name)) {
                    uniqueSpansMap.set(span.name, span)
                }
            })
            spans = Array.from(uniqueSpansMap.values())
        }

        const spanStatusCount: Record<string, number> = {}
        spans.forEach(span => {
            const status = span.properties?.["Completion Status"] || "Yet To Start"
            spanStatusCount[status] = (spanStatusCount[status] || 0) + 1
        })

        const spanCompletionData: ChartData[] = Object.entries(spanStatusCount).map(([name, value], index) => ({
            name,
            value,
            color: COMPLETION_STATUS_COLORS[name] || COLOR_PALETTE[index % COLOR_PALETTE.length],
        }))

        return {
            completionStatusData: completionData,
            structureTypeData: structureArray,
            completionStatuses: Array.from(uniqueCompletionStatuses),
            spanCompletionData: spanCompletionData
        }
    }, [selectedNode, projectData, isValidSelection])

    const totalTowers = completionStatusData.reduce((sum, item) => sum + item.value, 0)
    const totalSpans = spanCompletionData.reduce((sum, item) => sum + item.value, 0)

    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const entry = payload[0]
            return (
                <View style={tooltipStyles.container}>
                    <Text style={tooltipStyles.label}>{entry.name}</Text>
                    <Text style={tooltipStyles.value}>{entry.value} towers</Text>
                    <Text style={tooltipStyles.percentage}>
                        ({((entry.value / totalTowers) * 100).toFixed(1)}%)
                    </Text>
                </View>
            )
        }
        return null
    }

    const CustomSpanTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const entry = payload[0]
            return (
                <View style={tooltipStyles.container}>
                    <Text style={tooltipStyles.label}>{entry.name}</Text>
                    <Text style={tooltipStyles.value}>{entry.value} spans</Text>
                    <Text style={tooltipStyles.percentage}>
                        ({((entry.value / totalSpans) * 100).toFixed(1)}%)
                    </Text>
                </View>
            )
        }
        return null
    }

    const CustomStackedTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            const total = payload.reduce((sum: number, entry: any) => sum + entry.value, 0)
            return (
                <View style={tooltipStyles.container}>
                    <Text style={tooltipStyles.label}>{label}</Text>
                    <View style={tooltipStyles.divider} />
                    {payload.map((entry: any, index: number) => (
                        entry.value > 0 && (
                            <View key={`item-${index}`} style={tooltipStyles.item}>
                                <View style={[tooltipStyles.colorBox, { backgroundColor: entry.color }]} />
                                <Text style={tooltipStyles.name}>{entry.name}:</Text>
                                <Text style={tooltipStyles.value}>{entry.value} towers</Text>
                            </View>
                        )
                    ))}
                    <View style={tooltipStyles.divider} />
                    <View style={tooltipStyles.totalRow}>
                        <Text style={tooltipStyles.totalLabel}>Total:</Text>
                        <Text style={tooltipStyles.totalValue}>{total} towers</Text>
                    </View>
                </View>
            )
        }
        return null
    }

    // Show message if tower or span is selected
    if (!isValidSelection) {
        return (
            <View>
                <HorizontalSection title="Tower Erection Status" content={{}} defaultExpanded={true}>
                    <View style={styles.messageContainer}>
                        <Text style={[styles.messageText, { color: theme.colors.onSurfaceVariant }]}>
                            Please select a line or section to view the overview report
                        </Text>
                    </View>
                </HorizontalSection>
            </View>
        )
    }

    return (
        <View>
            {/* Tower Section */}
            <HorizontalSection title="Tower Erection Status" content={{}} defaultExpanded={true}>
                <View style={styles.chartsContainer}>
                    {/* Left: Donut Chart - Completion Status */}
                    <View style={styles.chartWrapper}>
                        <Text style={styles.chartTitle}>Completion Status Distribution</Text>
                        <ResponsiveContainer width="100%" height={400}>
                            <PieChart>
                                <Pie
                                    data={completionStatusData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={120}
                                    paddingAngle={2}
                                >
                                    {completionStatusData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                                <Legend
                                    verticalAlign="bottom"
                                    height={36}
                                    formatter={(value, entry) => {
                                        const percentage = ((entry.payload.value / totalTowers) * 100).toFixed(1)
                                        return `${value}: ${entry.payload.value} (${percentage}%)`
                                    }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                        {completionStatusData.length > 0 && (
                            <View style={styles.centerLabel}>
                                <Text style={styles.centerValue}>{totalTowers}</Text>
                                <Text style={styles.centerText}>Total Towers</Text>
                            </View>
                        )}
                    </View>

                    {/* Right: Stacked Column Chart - Tower Count by Structure Type and Completion Status */}
                    <View style={styles.chartWrapper}>
                        <Text style={styles.chartTitle}>Tower Count by Structure Type</Text>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart data={structureTypeData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="structureType" angle={-45} textAnchor="end" height={100} />
                                <YAxis label={{ value: "Number of Towers", angle: -90, position: "insideLeft" }} />
                                <Tooltip content={<CustomStackedTooltip />} />
                                <Legend />
                                {completionStatuses.map((status) => (
                                    <Bar
                                        key={status}
                                        dataKey={status}
                                        stackId="a"
                                        fill={COMPLETION_STATUS_COLORS[status] || "#999"}
                                    />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </View>
                </View>
            </HorizontalSection>

            {/* Span Section */}
            <HorizontalSection title="Span Stringing Status" content={{}} defaultExpanded={true}>
                <View style={styles.spanContainer}>
                    <View style={styles.chartWrapper}>
                        <Text style={styles.chartTitle}>Completion Status Distribution</Text>
                        <ResponsiveContainer width="100%" height={400}>
                            <PieChart>
                                <Pie
                                    data={spanCompletionData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={120}
                                    paddingAngle={2}
                                >
                                    {spanCompletionData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomSpanTooltip />} />
                                <Legend
                                    verticalAlign="bottom"
                                    height={36}
                                    formatter={(value, entry) => {
                                        const percentage = ((entry.payload.value / totalSpans) * 100).toFixed(1)
                                        return `${value}: ${entry.payload.value} (${percentage}%)`
                                    }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                        {spanCompletionData.length > 0 && (
                            <View style={styles.centerLabel}>
                                <Text style={styles.centerValue}>{totalSpans}</Text>
                                <Text style={styles.centerText}>Total Spans</Text>
                            </View>
                        )}
                    </View>
                </View>
            </HorizontalSection>
        </View>
    )
}

const styles = StyleSheet.create({
    chartsContainer: {
        flexDirection: "row",
        gap: 32,
        paddingVertical: 16,
    },
    spanContainer: {
        flexDirection: "row",
        justifyContent: "center",
        paddingVertical: 16,
    },
    chartWrapper: {
        flex: 1,
        minWidth: 400,
        position: "relative",
    },
    chartTitle: {
        fontSize: 16,
        fontWeight: "600",
        marginBottom: 16,
        textAlign: "center",
    },
    centerLabel: {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: [{ translateX: -50 }, { translateY: -50 }],
        alignItems: "center",
        pointerEvents: "none",
    },
    centerValue: {
        fontSize: 32,
        fontWeight: "bold",
    },
    centerText: {
        fontSize: 14,
        color: "#666",
    },
    messageContainer: {
        padding: 40,
        alignItems: "center",
        justifyContent: "center",
    },
    messageText: {
        fontSize: 16,
        textAlign: "center",
    },
})

const tooltipStyles = StyleSheet.create({
    container: {
        backgroundColor: "white",
        padding: 12,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: "#ddd",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
    },
    label: {
        fontWeight: "600",
        marginBottom: 8,
        fontSize: 14,
    },
    value: {
        fontSize: 14,
        color: "#333",
    },
    percentage: {
        fontSize: 12,
        color: "#666",
        marginTop: 4,
    },
    divider: {
        height: 1,
        backgroundColor: "#eee",
        marginVertical: 8,
    },
    item: {
        flexDirection: "row",
        alignItems: "center",
        marginVertical: 4,
    },
    colorBox: {
        width: 12,
        height: 12,
        marginRight: 8,
        borderRadius: 2,
    },
    name: {
        fontSize: 12,
        marginRight: 4,
    },
    totalRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginTop: 4,
    },
    totalLabel: {
        fontSize: 12,
        fontWeight: "600",
    },
    totalValue: {
        fontSize: 12,
        fontWeight: "600",
    },
})
